import { useCallback, useState, useRef, useEffect } from "react";
import { isObject } from "../utils/objects";

function readLocalStorageValue(key) {
  let item = localStorage.getItem(key);
  item = item ? JSON.parse(item) : null;

  //if value was found in local storage, but not of the format
  // {value:<actual value>}, then it is not the correct format for this hook
  if (item && item.value === undefined) {
    throw new Error(
      "The local storage value found was not in the proper format: missing the value key on the object"
    );
  }

  //if the stored item has an expiration date, check date
  //if expired, return null and delete the value from storage,
  //otherwise return the value
  if (
    isObject(item) &&
    item.hasOwnProperty("expirationDate") &&
    item.hasOwnProperty("value")
  ) {
    const now = new Date();

    // compare the expiration time of the item with the current time
    if (now.getTime() > item.expirationDate) {
      localStorage.removeItem(key);
      return null;
    } else {
      return item.value;
    }
  }

  //item in local storage was a non-ttl object format, return it
  // if it doesn't exist, returned value will be null
  return item?.value || null;
}

const getObjectToStore = (value, ttl) => {
  const objToStore = { value };

  //if a ttl is provided, add the expiration date to the object
  if (ttl > 0) {
    const now = new Date();
    objToStore.expirationDate = now.getTime() + ttl;
  }
  return objToStore;
};

/**
 *
 * @param {string} key
 * @param {*} initialValue
 * @param {integer} ttl
 * @returns array
 *
 * hook which will use local storage to read and store a key/value pair
 * if a ttl, in milliseconds, is provided, the value will only stay alive
 * for the given time period
 *
 */
export const useLocalStorage = (key, initialValue = null, ttl = 0) => {
  const resolverRef = useRef(null);

  //the stored value will always be held in an object
  // with a key 'value' for the stored value
  // and optional key 'ttl' for expiration date timestamp
  const [storedValue, setStoredValue] = useState(() => {
    try {
      //if an inital value is sent in, write it to the local store
      if (initialValue) {
        const objToStore = getObjectToStore(initialValue, ttl);
        localStorage.setItem(key, JSON.stringify(objToStore));
        return objToStore;
      }
      return { value: readLocalStorageValue(key) };
    } catch (err) {
      console.error(err);
      return { value: initialValue };
    }
  });

  const setValue = value => {
    try {
      const objToStore = getObjectToStore(value, ttl);
      localStorage.setItem(key, JSON.stringify(objToStore));
      setStoredValue(objToStore);
      return objToStore;
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * calling refreshStoredValue will return a Promise which can be awaited
   * the resolve callback is stored to a ref
   * the following useEffect listens for a change in the state
   * if there is a chage, it will call the resolve function stored in the ref
   * thus resolving the promise
   */
  const refreshStoredValue = useCallback(() => {
    setStoredValue({ value: readLocalStorageValue(key) });
    return new Promise(resolve => (resolverRef.current = resolve));
  }, [key]);

  /**
   * since we are using an object for storedValue, any change to it will trigger this effect
   * if we have a resolve function from the promise in the ref, call it and clear it
   */
  useEffect(() => {
    if (resolverRef.current) {
      resolverRef.current(storedValue.value);
      resolverRef.current = null;
    }
  }, [storedValue]);

  return [storedValue.value, setValue, refreshStoredValue];
};

export default useLocalStorage;
